import { useTeam } from '@vrplatform/data';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { formatCurrency, toTitleCase } from '@vrplatform/utils';
import { Box, Text } from '@mantine/core';
import { useMemo } from 'react';
import type {
  TrialBalanceQueryData,
  TrialBalanceRow,
} from './useTrialBalanceTableQuery';

export const useTrialBalanceTableColumns = ({
  totals,
}: {
  totals: TrialBalanceQueryData['totals'] | undefined;
}) => {
  const [{ currency }] = useTeam();
  return useMemo<MRT_ColumnDef<TrialBalanceRow>[]>(
    () => [
      {
        header: 'Account',
        accessorKey: 'title',
        Cell: ({ row }) => {
          if (!row.original.type)
            return (
              <Text display="block" truncate>
                {row.original.title}
              </Text>
            );

          return (
            <Box w="100%">
              <Text maw="100%" component="p" lineClamp={3} display="block">
                {row.original.title}
              </Text>
              <Text
                size="xs"
                component="p"
                c="neutral"
                lineClamp={2}
                display="block"
              >
                {row.original.type}
              </Text>
            </Box>
          );
        },
        mantineTableBodyCellProps: (props) => ({
          sx: (theme) => ({
            backgroundColor: props.cell.row.parentId
              ? theme.colors.neutral[1]
              : undefined,
          }),
        }),
      },
      {
        header: 'Classification',
        accessorKey: 'classification',
        maxSize: 0,
        minSize: 150,
        size: 150,
        Cell: ({ row }) => toTitleCase(row.original.classification),
        mantineTableBodyCellProps: (props) => ({
          sx: (theme) => ({
            backgroundColor: props.cell.row.parentId
              ? theme.colors.neutral[1]
              : undefined,
          }),
        }),
      },
      {
        header: 'Starting Balance',
        accessorFn: (row) =>
          formatCurrency((row.balanceStart ?? 0) / 100, currency),
        footer: totals?.balanceStart,
        mantineTableFooterCellProps: {
          align: 'right',
        },
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        maxSize: 75,
      },
      {
        header: 'Debit',
        accessorFn: (row) => formatCurrency((row.debit ?? 0) / 100, currency),
        footer: totals?.debit,
        mantineTableFooterCellProps: {
          align: 'right',
        },
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        maxSize: 75,
      },
      {
        accessorFn: (row) => formatCurrency((row.credit ?? 0) / 100, currency),
        footer: totals?.credit,
        header: 'Credit',
        mantineTableFooterCellProps: {
          align: 'right',
        },
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        maxSize: 75,
      },
      {
        header: 'Ending Balance',
        footer: totals?.balanceEnd,
        accessorFn: (row) =>
          formatCurrency((row.balanceEnd ?? 0) / 100, currency),
        mantineTableFooterCellProps: {
          align: 'right',
        },
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        maxSize: 75,
      },
    ],
    [
      totals?.credit,
      totals?.debit,
      totals?.balanceEnd,
      totals?.balanceStart,
      currency,
    ]
  );
};
